// ComprehendLive.js — the ambient agent command center.
// Mounts between the navbar and routed content (layouts/User.js) behind the
// `comprehend_live` feature flag. The collapsed rail is always visible; the
// expanded panel EMERGES from the rail's exact bounds — growing to fill the
// screen as though the agent lives underneath the UI — and contracts back
// into the banner on close. Designed 350px-first for the extension panel.
// See docs/comprehend-live.md.
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ComprehendLiveProvider,
  useComprehendLive,
} from 'agent/useComprehendLive';
import CollapsedRail from './CollapsedRail';
import ExpandedPanel from './ExpandedPanel';
import MultiRecordTray from './MultiRecordTray';
import MultiRecordEngine from './MultiRecordEngine';
import ListeningAura from './ListeningAura';
import { LIVE_KEYFRAMES, T } from './liveTheme';

const EMERGE_MS = 480;
const EMERGE_EASE = 'cubic-bezier(.22, .9, .3, 1)';

const Shell = () => {
  const { state, actions } = useComprehendLive();
  const railRef = useRef(null);
  const wrapperRef = useRef(null);
  const pushedElsRef = useRef([]);
  const [panelRect, setPanelRect] = useState(null);
  // Animation phases: closed -> opening (mounted at rail bounds) ->
  // open (grown to full screen) -> closing (shrinking back) -> closed.
  const [rendered, setRendered] = useState(false);
  const [open, setOpen] = useState(false);
  // The fixed full-width top bar + push-the-app-down only makes sense when
  // Argon's fixed-left sidebar is showing; below that breakpoint the sidebar
  // collapses to a top hamburger and main-content is already full width, so
  // the rail just lives in normal flow. Detect the EXACT switch by reading
  // main-content's margin-left — Argon sets it (250px) only while the
  // fixed-left sidebar is active (`.fixed-left + .main-content`), whatever
  // the breakpoint (it's navbar-expand-md = 768px here). Beats a hardcoded
  // width guess that mismatched in the 768–1199 band.
  const detectWide = () => {
    const mc = document.querySelector('.main-content');
    if (!mc) return false;
    return (parseFloat(window.getComputedStyle(mc).marginLeft) || 0) > 0;
  };
  const [wideLayout, setWideLayout] = useState(detectWide);
  useEffect(() => {
    const onResize = () => setWideLayout(detectWide());
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Mount/unmount around the grow/shrink animation.
  useEffect(() => {
    if (state.expanded) {
      const r = railRef.current?.getBoundingClientRect();
      if (r) {
        setPanelRect({
          top: r.top,
          left: r.left,
          width: r.width,
          height: r.height,
        });
      }
      setRendered(true);
      const raf = requestAnimationFrame(() =>
        requestAnimationFrame(() => setOpen(true)),
      );
      return () => cancelAnimationFrame(raf);
    }
    if (rendered) {
      setOpen(false); // contract back into the banner...
      const t = setTimeout(() => setRendered(false), EMERGE_MS);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.expanded]);

  // Scroll-lock the app while the panel is up (incl. during the close
  // animation) — wheel events would otherwise fall through to the long
  // page beneath.
  useEffect(() => {
    if (!rendered) return undefined;
    const prevOverflow = document.body.style.overflow;
    const prevPadding = document.body.style.paddingRight;
    // Hiding the page scrollbar widens the layout AFTER the rail was
    // measured, leaving the panel narrower than the viewport. Pad by the
    // scrollbar's width so nothing shifts while locked.
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPadding;
    };
  }, [rendered]);

  // Reserve a full-width strip at the very top of the APP for the rail, and
  // push everything else below it — the fixed Argon sidebar (#sidenav-main)
  // drops by the rail height, and the routed content (incl. its navbar,
  // search, and new-visit controls) flows down via body padding-top. This is
  // why the rail can span the full width over the sidebar and never sits in
  // front of the page controls.
  useEffect(() => {
    const el = wrapperRef.current;
    // Narrow layout: rail is in normal flow (full-width already), nothing to
    // reserve — leave the page alone.
    if (!el || !wideLayout) return undefined;
    const sidebar = document.getElementById('sidenav-main');
    const prevBodyPad = document.body.style.paddingTop;
    const prevSidebarTop = sidebar ? sidebar.style.top : '';
    const apply = () => {
      const h = el.offsetHeight || 0;
      document.body.style.paddingTop = `${h}px`;
      if (sidebar) sidebar.style.top = `${h}px`;
    };
    apply();
    // Debounce the resize -> layout write to the next frame. apply() mutates
    // body padding, which can resize `el` again; running it synchronously in
    // the observer is what trips "ResizeObserver loop ...".
    let raf = null;
    const onResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };
    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(onResize)
        : null;
    ro?.observe(el);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro?.disconnect();
      document.body.style.paddingTop = prevBodyPad;
      if (sidebar) sidebar.style.top = prevSidebarTop;
    };
  }, [wideLayout]);

  // Full-screen now COVERS the whole viewport (the panel grows to 100vw ×
  // 100vh over the sidebar + navbar), so we no longer push siblings apart.
  // Push-apart could never reach the sidebar anyway — it's a sibling of
  // main-content, not a child — which is exactly why the old behavior left
  // the sidebar visible and shoved only the search/new-visit. Cover, don't
  // shove. (pushedElsRef retained as a harmless no-op for the cleanup below.)

  // Once fully closed, clear any inline styles we may have left behind.
  useEffect(() => {
    if (rendered) return;
    pushedElsRef.current.forEach(({ el }) => {
      el.style.transition = '';
      el.style.transform = '';
    });
    pushedElsRef.current = [];
  }, [rendered]);

  useEffect(() => {
    if (!state.expanded) return undefined;
    const measure = () => {
      const r = railRef.current?.getBoundingClientRect();
      if (r) {
        setPanelRect({
          top: r.top,
          left: r.left,
          width: r.width,
          height: r.height,
        });
      }
    };
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [state.expanded]);

  return (
    <div
      ref={wrapperRef}
      style={
        wideLayout
          ? {
              // Wide: full-width strip pinned to the top of the app, above
              // the sidebar + navbar (which we push down by its height).
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1041,
            }
          : {
              // Narrow (extension / mobile): in normal flow — main-content
              // is already full width here, so the rail spans it naturally.
              position: 'relative',
              zIndex: 1040,
            }
      }
    >
      <style>{LIVE_KEYFRAMES}</style>
      {/* Brand tie-in: the app's green gradient as a hairline accent */}
      <div style={{ height: 3, background: T.gradient }} />
      <div ref={railRef}>
        <CollapsedRail />
      </div>
      {/* Headless recorder for MultiRecord++ — must live OUTSIDE the expanded
          panel so recording continues while the tray is collapsed. */}
      {(state.trayMode === 'multirecord' || state.panelView === 'timeline') && (
        <MultiRecordEngine />
      )}
      {/* Siri-style edge aura — frames the whole screen while listening */}
      <ListeningAura />
      {rendered &&
        panelRect &&
        // Portal to <body>: this wrapper is a stacking context (relative +
        // zIndex 1040), so WITHOUT the portal even zIndex 10000001 children
        // are capped at 1040 and the help button (FloatingBugButton,
        // zIndex 9999999, mounted at body level) floats over the send
        // button. The portal escapes the context entirely.
        createPortal(
          <>
            {/* Click-away backdrop fades with the emergence */}
            <div
              onClick={() => actions.setExpanded(false)}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(13,35,30,0.35)',
                opacity: open ? 1 : 0,
                transition: `opacity ${EMERGE_MS}ms ease`,
                zIndex: 10000000,
              }}
            />
            <div
              style={{
                position: 'fixed',
                // The agent emerges FROM the banner: grows from the rail's
                // exact bounds to fully span the viewport — over the sidebar,
                // search, and new-visit buttons too (zIndex sits above
                // everything) — and contracts back into the banner on close.
                top: open ? 0 : panelRect.top,
                height: open ? '100vh' : panelRect.height,
                left: open ? 0 : panelRect.left,
                width: open ? '100vw' : panelRect.width,
                zIndex: 10000001,
                display: 'flex',
                overflow: 'hidden',
                borderRadius: open ? 0 : 8,
                // A soft green aura while it moves — the agent surfacing.
                boxShadow: open
                  ? '0 0 0 rgba(107,216,136,0)'
                  : '0 0 42px rgba(107,216,136,0.35)',
                // Grow ALL four edges from the rail rect to the full viewport
                // so it sweeps over the sidebar + navbar too.
                transition: `top ${EMERGE_MS}ms ${EMERGE_EASE}, left ${EMERGE_MS}ms ${EMERGE_EASE}, height ${EMERGE_MS}ms ${EMERGE_EASE}, width ${EMERGE_MS}ms ${EMERGE_EASE}, border-radius ${EMERGE_MS}ms ease, box-shadow ${EMERGE_MS}ms ease`,
              }}
            >
              {/* multirecord mode always shows the timeline; an ambient
                  user can toggle to it (state.panelView) for a demo. */}
              {state.trayMode === 'multirecord' ||
              state.panelView === 'timeline' ? (
                <MultiRecordTray />
              ) : (
                <ExpandedPanel />
              )}
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};

const ComprehendLive = () => (
  <ComprehendLiveProvider>
    <Shell />
  </ComprehendLiveProvider>
);

export default ComprehendLive;
